import type { PermissionId } from '@crm/permissions';
import type { ComponentType } from 'react';

/**
 * The settings registry: every category and item of the settings window comes from here.
 *
 * The core declares its own entries (`modules/settings/settings-entries.ts`); a plugin will
 * bring its entries through its manifest, and the composition root (`routes/_app.tsx`)
 * merges them with `mergeSettings`. The window never names an item: it lists what the
 * registry has and what the session may see.
 *
 * Ids are English and are what the deep link carries (`?settings=organization.units`): a
 * category id (`organization`) and an item id prefixed with its category
 * (`organization.units`).
 */

export type SettingsIcon = ComponentType<{ className?: string }>;

export interface SettingsCategory {
  /** English, kebab-case: `organization`, `team`, `plugins`. */
  id: string;
  /** i18n key of the label. */
  labelKey: string;
  /** `Regular` icon of 20. */
  icon: SettingsIcon;
  /** `Filled` icon of 20, shown when selected. */
  selectedIcon: SettingsIcon;
  /** Position among the categories (ascending). */
  order: number;
}

export interface SettingsItem {
  /** `<category>.<item>`, English, kebab-case: `organization.units`. */
  id: string;
  /** Id of the category it belongs to. */
  category: string;
  /** i18n key of the label (also the title of the content column). */
  labelKey: string;
  /** i18n key of extra search words (comma or space separated), on top of the label. */
  keywordsKey?: string;
  /** The item shows only when the session holds this permission. */
  permission: PermissionId;
  /** The content of the item (column 3). Lazy components are welcome. */
  component: ComponentType;
  /** Position inside the category (ascending). */
  order: number;
}

export interface SettingsRegistry {
  categories: readonly SettingsCategory[];
  items: readonly SettingsItem[];
}

export class InvalidSettingsRegistryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = InvalidSettingsRegistryError.name;
  }
}

const ID_SEGMENT = /^[a-z][a-z0-9-]*$/;

/**
 * Declares settings entries, checking the contract: ids in English kebab-case, an item id
 * prefixed by its category, no duplicate. A broken entry fails at startup, not on screen.
 */
export function defineSettings(registry: SettingsRegistry): SettingsRegistry {
  return mergeSettings(registry);
}

/** Merges the core entries with the ones of plugins; same checks as `defineSettings`. */
export function mergeSettings(...registries: readonly SettingsRegistry[]): SettingsRegistry {
  const categories = registries.flatMap((registry) => registry.categories);
  const items = registries.flatMap((registry) => registry.items);
  const categoryIds = new Set<string>();
  for (const category of categories) {
    if (!ID_SEGMENT.test(category.id)) {
      throw new InvalidSettingsRegistryError(`Invalid settings category id "${category.id}".`);
    }
    if (categoryIds.has(category.id)) {
      throw new InvalidSettingsRegistryError(`Duplicate settings category "${category.id}".`);
    }
    categoryIds.add(category.id);
  }
  const itemIds = new Set<string>();
  for (const item of items) {
    const [prefix, name, ...rest] = item.id.split('.');
    if (
      prefix !== item.category ||
      name === undefined ||
      rest.length > 0 ||
      !ID_SEGMENT.test(name)
    ) {
      throw new InvalidSettingsRegistryError(
        `Settings item id "${item.id}" must be "${item.category}.<item>".`,
      );
    }
    // A plugin may add items to a category declared by another registry; it only needs to exist.
    if (!categoryIds.has(item.category)) {
      throw new InvalidSettingsRegistryError(
        `Settings item "${item.id}" points to the unknown category "${item.category}".`,
      );
    }
    if (itemIds.has(item.id)) {
      throw new InvalidSettingsRegistryError(`Duplicate settings item "${item.id}".`);
    }
    itemIds.add(item.id);
  }
  return { categories, items };
}

/** A category the session sees, with its visible items in order. */
export interface VisibleSettingsCategory extends SettingsCategory {
  items: SettingsItem[];
}

/**
 * What the session sees: items it has permission for, categories with at least one visible
 * item, both in their declared order.
 */
export function visibleSettings(
  registry: SettingsRegistry,
  can: (permission: PermissionId) => boolean,
): VisibleSettingsCategory[] {
  return registry.categories
    .toSorted((a, b) => a.order - b.order)
    .map((category): VisibleSettingsCategory =>
      Object.assign({}, category, {
        items: registry.items
          .filter((item) => item.category === category.id && can(item.permission))
          .toSorted((a, b) => a.order - b.order),
      }),
    )
    .filter((category) => category.items.length > 0);
}

/** Lowercase, without accents: "Feriádo" and "feriado" match. */
export function normalizeSearchText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

export interface SettingsSearchResult {
  item: SettingsItem;
  category: VisibleSettingsCategory;
}

/**
 * Items whose label, keywords or category label contain EVERY word typed, across
 * categories, in display order. An empty query finds nothing.
 */
export function searchSettings(
  visible: readonly VisibleSettingsCategory[],
  query: string,
  translate: (key: string) => string,
): SettingsSearchResult[] {
  const terms = normalizeSearchText(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  const results: SettingsSearchResult[] = [];
  for (const category of visible) {
    const categoryText = translate(category.labelKey);
    for (const item of category.items) {
      const haystack = normalizeSearchText(
        [
          translate(item.labelKey),
          item.keywordsKey ? translate(item.keywordsKey) : '',
          categoryText,
        ].join(' '),
      );
      if (terms.every((term) => haystack.includes(term))) results.push({ item, category });
    }
  }
  return results;
}

/** The search param of the deep link: `?settings=organization.units`. */
export const SETTINGS_SEARCH_PARAM = 'settings';

/** The value the deep link carries for an item. */
export function settingsLinkValue(item: SettingsItem): string {
  return item.id;
}

/**
 * The item a deep-link value opens, among what the session sees: the item itself; for a
 * category id, its first item; for anything else (unknown, or not allowed), the first visible
 * item. `null` when the session sees no settings at all, or the value is not a string.
 */
export function resolveSettingsLink(
  visible: readonly VisibleSettingsCategory[],
  value: unknown,
): SettingsItem | null {
  if (typeof value !== 'string' || value === '') return null;
  const first = visible[0]?.items[0] ?? null;
  const items = visible.flatMap((category) => category.items);
  const exact = items.find((item) => item.id === value);
  if (exact) return exact;
  const category = visible.find((candidate) => candidate.id === value.split('.')[0]);
  return category?.items[0] ?? first;
}
